import React from 'react'
import Icon from '@/components/Icon'

export interface LoadingSpinnerProps {
  /**
   * Size of the spinner (matches fontawesome size values)
   */
  size?: 'sm' | 'lg'
  /**
   * Text to display as screen reader text (for accessibility)
   */
  srText?: string
  /**
   * Inverted color variant of the spinner
   */
  inverted?: boolean
  /**
   * Optional test ID for the component
   */
  'data-testid'?: string
}

/**
 * ```
 * import { LoadingSpinner } from '@/components/LoadingSpinner'
 *
 * <LoadingSpinner size="2x" variant="primary" />
 * ```
 * A reusable loading spinner component that can be used to indicate loading states across the application.
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'sm',
  srText = 'Loading...',
  inverted = false,
  'data-testid': testId,
}) => {
  return (
    <div
      className='flex items-center justify-center'
      role='status'
      aria-live='polite'
      data-testid={testId}
    >
      <Icon
        icon='spinner'
        size={size}
        spin
        className={inverted ? 'text-white' : 'text-primary'}
        aria-hidden='true'
      />
      {srText && <span className='sr-only'>{srText}</span>}
    </div>
  )
}

export default LoadingSpinner
